import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { sizes } from '../ids-icon/ids-icon-attributes';
import { IdsColorValue } from '../../utils/ids-color-utils/ids-color-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsTooltipMixin from '../../mixins/ids-tooltip-mixin/ids-tooltip-mixin';

import IdsElement from '../../core/ids-element';

import '../ids-icon/ids-icon';
import styles from './ids-alert.scss';

export type IdsAlertTypes = 'success' | 'info' | 'warning' | 'error' | 'in-progress' | 'new' | 'caution' | 'alert';

const Base = IdsTooltipMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS Alert Component
 * @type {IdsAlert}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsTooltipMixin
 * @part icon - the icon element
 */
@customElement('ids-alert')
@scss(styles)
export default class IdsAlert extends Base {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  /**
   * Set the alert color on the tooltip (mixin function)
   * @param {any} tooltip the tooltip element
   */
  beforeTooltipShow(tooltip?: any) {
    // Color the tooltip
    if (tooltip.popup) {
      const hasColor = this.toolTipTarget.getAttribute('color');
      tooltip.popup?.container?.classList.add(`${hasColor || this.toolTipTarget.getAttribute('icon')}-color`);
      tooltip.popup.onPlace = (popupRect: DOMRect) => {
        const tooltipInstane = tooltip;
        // deduct more when tooltip shows on bottom instead on top
        if (tooltipInstane?.popup?.arrowEl?.classList?.contains?.('bottom')
          && tooltipInstane?.popup?.align === 'top') {
          popupRect.y -= 6;
        } else {
          popupRect.y -= 4;
        }

        return popupRect;
      };
    }
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The propertires in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.COLOR,
      attributes.DISABLED,
      attributes.ICON,
      attributes.TOOLTIP,
      attributes.SIZE
    ];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const cssClass = stringToBool(this.disabled) ? ' class="disabled"' : '';
    const iconName = this.icon && ['warning', 'caution'].includes(this.icon) ? 'alert' : this.icon;
    return `<ids-icon size="${this.size}"${cssClass} icon="${iconName}" part="icon"></ids-icon>`;
  }

  /**
   * Set the alert color
   * @param {IdsColorValue} value The color to use between: error, success, info, ect..
   */
  set color(value: IdsColorValue) {
    if (value) {
      this.setAttribute(attributes.COLOR, value);
      this.shadowRoot?.querySelector('ids-icon')?.setAttribute(attributes.COLOR, value);
    } else {
      this.removeAttribute(attributes.COLOR);
      this.shadowRoot?.querySelector('ids-icon')?.removeAttribute(attributes.COLOR);
    }
  }

  get color(): IdsColorValue {
    return this.getAttribute(attributes.COLOR) as IdsColorValue;
  }

  /**
   * Sets the disabled state
   * @param {boolean | string} value true/false to set disabled/enabled
   */
  set disabled(value: boolean | string) {
    const icon = this.shadowRoot?.querySelector('ids-icon');
    const val = stringToBool(value);
    const validIcons = new Set<IdsAlertTypes>(['success', 'info', 'warning', 'error', 'in-progress', 'new', 'caution', 'alert']);
    if (val) {
      this.setAttribute(attributes.DISABLED, val.toString());
      if (!validIcons.has(this.icon as IdsAlertTypes)) {
        icon?.classList.add('custom-disabled');
      } else {
        icon?.classList.add(attributes.DISABLED);
      }
    } else {
      this.removeAttribute(attributes.DISABLED);
      icon?.classList.remove(attributes.DISABLED, 'custom-disabled');
    }
  }

  get disabled(): boolean | string {
    return stringToBool(this.getAttribute(attributes.DISABLED));
  }

  /**
   * Return the icon of the alert.
   * @returns {string | null} the path data
   */
  get icon(): string | null { return this.getAttribute(attributes.ICON); }

  /**
   * Set the icon
   * @param {string | null} value The Icon Type [success, info, error, warning]
   */
  set icon(value: string | null) {
    if (value) {
      this.setAttribute(attributes.ICON, value);
      const iconName = ['warning', 'caution'].includes(value) ? 'alert' : value;
      const iconElem = this.shadowRoot?.querySelector('ids-icon');
      iconElem?.setAttribute(attributes.ICON, iconName);
      if (!this.color) {
        iconElem?.setAttribute(attributes.COLOR, value);
      }
    } else {
      this.removeAttribute(attributes.ICON);
    }
  }

  /**
   * Set the size. May be large, normal/medium or small
   * @param {string} value the size
   */
  set size(value: string | null) {
    if (value && sizes[value]) {
      this.setAttribute(attributes.SIZE, value);
      this.shadowRoot?.querySelector('ids-icon')?.setAttribute(attributes.SIZE, value);
    } else {
      this.removeAttribute(attributes.SIZE);
      this.shadowRoot?.querySelector('ids-icon')?.removeAttribute(attributes.SIZE);
    }
  }

  get size(): string { return this.getAttribute(attributes.SIZE) || 'normal'; }
}
